// Interactive board for one venue, ported from radar's stocks table:
// resizable columns, drag to reorder, a compact filter dropdown on every
// header, and a CSV export of exactly what the filters leave visible.
// A withheld figure renders as unavailable, never as zero, and sorts last
// whichever way the column is pointed.

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnSizingState,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

import { cleanFlow, FLOW_EPSILON, FLOW_WINDOWS, flowMethodText, flowText, flowTone, type FlowMethod, type FlowWindow } from "../flows";
import { amount, compactUsd, percent, price, signedPercent } from "../format";
import { rovingIndex } from "../roving";
import styles from "./StocksTable.module.css";

export interface StockRow {
  /** Unique row id: the API token id, or `<token id>@<chain>` for one chain of a token. */
  id: string;
  /** The API token id the token panel opens, when it differs from `id` (per-chain rows). */
  tokenId?: string;
  /** The row's chain id (per-chain rows), for filtering. */
  chainId?: string;
  /**
   * The chain's market cap is an upper bound: the token's per-chain supplies include bridged or
   * inventory copies, so supply × price on one chain can exceed what is really there.
   */
  mcapUpper?: boolean;
  /** The whole token's market cap, on per-chain rows whose own value is only an upper bound. */
  tokenCap?: number | null;
  symbol: string;
  name: string;
  address: string;
  explorerUrl: string | null;
  /** Issuing venue; shown only when the table is given `venueColumn`. */
  venue?: string;
  /** Chains the token is deployed on, as display labels. */
  chains: string;
  halted: boolean | null;
  price: number | null;
  /** Why the price is not the venue's own quote; shown as a note on the price cell. */
  priceNote?: string | null;
  change: number | null;
  mcap: number | null;
  supply: number | null;
  share: number | null;
  volume: number | null;
  mintBurn: number | null;
  /** Net flow over 1, 7 and 30 days, USD (C-6c); the grid shows the window picked in its toolbar. */
  netFlow?: number | null;
  netFlow7d?: number | null;
  netFlow30d?: number | null;
  flowMethod?: FlowMethod | null;
}

type Kind = "text" | "usd" | "usdPrice" | "supply" | "pct" | "change" | "flow";
type ColumnId = Exclude<keyof StockRow, "id" | "name" | "address" | "explorerUrl" | "halted" | "priceNote" | "netFlow7d" | "netFlow30d" | "flowMethod">;

/** The row's net flow for the picked window, |x| under a cent as 0. */
const rowFlow = (row: StockRow, window: FlowWindow): number | null =>
  cleanFlow(window === "1d" ? row.netFlow : window === "7d" ? row.netFlow7d : row.netFlow30d);

interface FilterOption {
  label: string;
  min?: number;
  max?: number;
}

type FilterValue = string | { min?: number; max?: number };

interface ColumnSpec {
  id: ColumnId;
  label: string;
  kind: Kind;
  size: number;
  options: FilterOption[];
}

function formatValue(kind: Kind, value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "unavailable";
  if (typeof value === "string") return value;
  switch (kind) {
    case "usd":
      return compactUsd(value);
    case "usdPrice":
      return price(value);
    case "supply":
      return amount(value);
    case "pct":
      return percent(value);
    case "change":
      return signedPercent(value);
    case "flow":
      return flowText(value);
    default:
      return String(value);
  }
}

/** What a missing figure says in the grid, instead of "unavailable". */
const MISSING: Partial<Record<ColumnId, string>> = {
  price: "no price",
  change: "priced <24h",
  mcap: "no cap",
  supply: "not read",
  share: "not split",
  volume: "not reported",
  mintBurn: "not measured",
  netFlow: "not measured",
};

const MISSING_TITLE: Partial<Record<ColumnId, string>> = {
  price: "The venue published no price in the last collection",
  change: "No price 24 hours earlier to compare with: the token was unpriced or not yet listed then",
  share: "A chain's share is not split when its supply includes bridged copies",
  mcap: "Needs both a price and a supply",
  supply: "Supply not read on its chains in the last collection",
  volume: "The venue does not report trading volume",
  mintBurn: "Mint and burn are read only where our transfer index covers the token",
  netFlow: "Needs a daily snapshot of the day before",
};

const atLeast = (labels: Array<[string, number]>): FilterOption[] => labels.map(([label, min]) => ({ label, min }));

const COLUMNS: ColumnSpec[] = [
  { id: "symbol", label: "Token", kind: "text", size: 250, options: [] },
  { id: "venue", label: "Venue", kind: "text", size: 120, options: [] },
  { id: "chains", label: "Chains", kind: "text", size: 150, options: [] },
  {
    id: "price",
    label: "Price",
    kind: "usdPrice",
    size: 120,
    options: [
      { label: "Under $10", max: 10 },
      { label: "$10 – $100", min: 10, max: 100 },
      { label: "$100 – $500", min: 100, max: 500 },
      { label: "$500 and up", min: 500 },
    ],
  },
  {
    id: "change",
    label: "24h",
    kind: "change",
    size: 100,
    options: [
      { label: "Up", min: 0.000001 },
      { label: "Down", max: -0.000001 },
      { label: "Up 5% or more", min: 0.05 },
      { label: "Down 5% or more", max: -0.05 },
    ],
  },
  {
    id: "mcap",
    label: "Market cap",
    kind: "usd",
    size: 130,
    options: atLeast([
      ["$10K and up", 10_000],
      ["$100K and up", 100_000],
      ["$1M and up", 1_000_000],
      ["$10M and up", 10_000_000],
    ]),
  },
  {
    id: "supply",
    label: "Supply",
    kind: "supply",
    size: 130,
    options: atLeast([
      ["1K and up", 1_000],
      ["10K and up", 10_000],
      ["100K and up", 100_000],
    ]),
  },
  {
    id: "share",
    label: "Share of cap",
    kind: "pct",
    size: 120,
    options: atLeast([
      ["0.1% and up", 0.001],
      ["1% and up", 0.01],
      ["5% and up", 0.05],
    ]),
  },
  {
    id: "volume",
    label: "Trading vol · day",
    kind: "usd",
    size: 140,
    options: atLeast([
      ["$100K and up", 100_000],
      ["$1M and up", 1_000_000],
      ["$10M and up", 10_000_000],
    ]),
  },
  {
    id: "mintBurn",
    label: "Mint + burn · day",
    kind: "usd",
    size: 140,
    options: atLeast([
      ["$10K and up", 10_000],
      ["$100K and up", 100_000],
      ["$1M and up", 1_000_000],
    ]),
  },
  {
    id: "netFlow",
    label: "Net flow",
    kind: "flow",
    size: 120,
    options: [
      { label: "Inflow", min: FLOW_EPSILON },
      { label: "Outflow", max: -FLOW_EPSILON },
    ],
  },
];

export function stocksCsv(rows: readonly StockRow[], columns: readonly ColumnSpec[]): string {
  const escape = (value: string | number | boolean | null | undefined) => {
    let text = value === null || value === undefined ? "" : String(value);
    // Neutralise spreadsheet formula injection from text cells.
    if (typeof value === "string" && /^[=+@\-\t\r\n]/.test(text)) text = `'${text}`;
    return `"${text.replaceAll('"', '""')}"`;
  };
  const headers = [...columns.map((c) => c.label), "Name", "Contract", "Halted"];
  const lines = rows.map((row) =>
    [...columns.map((c) => row[c.id]), row.name, row.address, row.halted].map(escape).join(","),
  );
  return [headers.map(escape).join(","), ...lines].join("\n");
}

function matchesFilter(raw: unknown, filter: FilterValue): boolean {
  if (typeof filter === "string") {
    const query = filter.trim().toLowerCase();
    return !query || String(raw ?? "").toLowerCase().includes(query);
  }
  if (typeof raw !== "number") return false;
  if (filter.min !== undefined && raw < filter.min) return false;
  if (filter.max !== undefined && raw > filter.max) return false;
  return true;
}

function Chevron({ active }: { active: boolean }) {
  return (
    <svg aria-hidden className={active ? styles.chevronActive : styles.chevron} fill="none" height="8" viewBox="0 0 10 6" width="10">
      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

export function StocksTable({
  rows,
  csvName = "tokenized-stocks.csv",
  venueColumn = false,
  onSelect,
  rowNoun = "tokens",
}: {
  rows: StockRow[];
  csvName?: string;
  /** What a row is, for the count: "tokens", or "token-chain rows" in the by-chain list. */
  rowNoun?: string;
  /** Adds a Venue column, for boards that mix venues. */
  venueColumn?: boolean;
  /** Given, the ticker opens the row (the explorer link becomes a small secondary link). */
  onSelect?: (row: StockRow) => void;
}) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [flowWindow, setFlowWindow] = useState<FlowWindow>("1d");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const dragged = useRef<string | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  /** Each header's filter toggle, so closing a dropdown can hand focus back to it. */
  const filterButtons = useRef(new Map<string, HTMLButtonElement>());
  const menuId = useId();
  const specById = useMemo(() => new Map(COLUMNS.map((spec) => [spec.id as string, spec])), []);

  /**
   * Closes the open dropdown. With `restoreFocus`, focus goes back to the
   * column's filter toggle first; otherwise it would fall to <body> when the
   * focused option or input unmounts.
   */
  function closeFilter(restoreFocus: boolean): void {
    if (restoreFocus && openFilter) filterButtons.current.get(openFilter)?.focus();
    setOpenFilter(null);
  }

  // A click outside the board, or Escape, closes an open filter dropdown.
  // Escape returns focus to the toggle when focus was in the board (or lost);
  // an outside click leaves focus where the click put it.
  useEffect(() => {
    if (!openFilter) return;
    const close = (event: MouseEvent) => {
      if (!boardRef.current?.contains(event.target as Node)) setOpenFilter(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const focus = document.activeElement;
      if (!focus || focus === document.body || boardRef.current?.contains(focus)) filterButtons.current.get(openFilter)?.focus();
      setOpenFilter(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [openFilter]);

  const columns = useMemo<Array<ColumnDef<StockRow>>>(
    () =>
      COLUMNS.filter((spec) => venueColumn || spec.id !== "venue").map((spec) => ({
        id: spec.id,
        // sortUndefined only recognises undefined, so a missing figure reaches the sorter that way.
        accessorFn: (row) => (spec.id === "symbol" ? `${row.symbol} ${row.name}` : spec.id === "netFlow" ? (rowFlow(row, flowWindow) ?? undefined) : (row[spec.id] ?? undefined)),
        size: spec.size,
        minSize: 90,
        sortUndefined: "last",
        sortDescFirst: spec.kind !== "text",
        filterFn: (row, columnId, filterValue: FilterValue) => matchesFilter(row.getValue(columnId), filterValue),
        header: spec.id === "netFlow" ? `Net flow · ${flowWindow}` : spec.label,
        cell: (context) => {
          const row = context.row.original;
          if (spec.id === "symbol") {
            return (
              <>
                {onSelect ? (
                  <button
                    className={styles.tokenButton}
                    onClick={(event) => {
                      // Browsers that do not focus a clicked button (Safari, Firefox on
                      // macOS) would otherwise leave the panel nowhere to return focus.
                      event.currentTarget.focus();
                      onSelect(row);
                    }}
                    title={`Open ${row.symbol} details`}
                    type="button"
                  >
                    {row.symbol}
                  </button>
                ) : row.explorerUrl ? (
                  <a className={styles.token} href={row.explorerUrl} rel="noreferrer" target="_blank" title="View contract on the explorer">
                    {row.symbol}
                  </a>
                ) : (
                  <span className={styles.token}>{row.symbol}</span>
                )}
                {onSelect && row.explorerUrl ? (
                  <a
                    aria-label={`${row.symbol} contract on the explorer`}
                    className={styles.explorer}
                    href={row.explorerUrl}
                    rel="noreferrer"
                    target="_blank"
                    title="View contract on the explorer"
                  >
                    ↗
                  </a>
                ) : null}
                <span className={styles.name}>{row.name}</span>
                {row.halted ? <span className={styles.halted}>halted</span> : null}
              </>
            );
          }
          const value = context.getValue<number | undefined>();
          if (spec.id === "price" && row.priceNote) {
            return (
              <>
                {formatValue(spec.kind, value)}
                <span className={styles.priceNote} tabIndex={0} title={row.priceNote}>
                  <span aria-hidden>ⓘ</span>
                  <span className="sr-only">Price note: {row.priceNote}</span>
                </span>
              </>
            );
          }
          if (spec.kind === "flow") {
            const flow = value ?? null;
            const tone = flowTone(flow);
            return (
              <span className={tone === "up" ? styles.up : tone === "down" ? styles.down : undefined} title={flowMethodText(row.flowMethod)}>
                {flowText(flow)}
              </span>
            );
          }
          if (spec.kind === "change" && typeof value === "number") {
            return <span className={value > 0 ? styles.up : value < 0 ? styles.down : undefined}>{formatValue(spec.kind, value)}</span>;
          }
          if (spec.id === "mcap" && row.mcapUpper && typeof value === "number") {
            return (
              <span title="This chain's supply includes bridged or inventory copies, so its value is at most this; the token's own cap is counted once in totals">
                ≤ {formatValue(spec.kind, value)}
              </span>
            );
          }
          if (value === null || value === undefined) {
            if (spec.id === "share" && !row.mcapUpper) return <span className={styles.missing}>no cap</span>;
            if (spec.id === "change" && row.price === null) {
              return (
                <span className={styles.missing} title="No price in the last collection, so no 24h change">
                  no price
                </span>
              );
            }
            // A chain of a multi-chain token: these figures are per token, on its one-chain rows only.
            if (row.tokenId && (spec.id === "netFlow" || spec.id === "volume" || spec.id === "mintBurn")) {
              return (
                <span className={styles.missing} title="Measured per token, not per chain">
                  per token
                </span>
              );
            }
            const reason = MISSING[spec.id] ?? "—";
            return (
              <span className={styles.missing} title={MISSING_TITLE[spec.id]}>
                {reason}
              </span>
            );
          }
          return formatValue(spec.kind, value);
        },
      })),
    [venueColumn, onSelect, flowWindow],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnFilters, columnOrder, columnSizing, sorting },
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Filters then sort: exactly what the table shows and what the CSV exports.
  const visibleRows = table.getRowModel().rows;

  function reorder(target: string): void {
    const from = dragged.current;
    dragged.current = null;
    if (!from || from === target) return;
    const order = table.getAllLeafColumns().map((column) => column.id);
    order.splice(order.indexOf(target), 0, ...order.splice(order.indexOf(from), 1));
    setColumnOrder(order);
  }

  function downloadCsv(): void {
    const specs = table.getAllLeafColumns().flatMap((column) => {
      const spec = specById.get(column.id);
      return spec ? [spec] : [];
    });
    // The net flow column exports the window on screen, labelled with it.
    const rowsOut = visibleRows.map((row) => ({ ...row.original, netFlow: rowFlow(row.original, flowWindow) }));
    const specsOut = specs.map((spec) => (spec.id === "netFlow" ? { ...spec, label: `Net flow ${flowWindow} (USD)` } : spec));
    const blob = new Blob([stocksCsv(rowsOut, specsOut)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = csvName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function filterMenu(spec: ColumnSpec): ReactNode {
    const column = table.getColumn(spec.id);
    if (!column) return null;
    const current = column.getFilterValue() as FilterValue | undefined;
    if (spec.kind === "text") {
      return (
        <div className={styles.menu} id={menuId}>
          <input
            aria-label={`Filter ${spec.label}`}
            autoFocus
            className={styles.menuInput}
            onChange={(event) => column.setFilterValue(event.target.value || undefined)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                closeFilter(true);
              }
            }}
            placeholder={spec.id === "chains" ? "chain…" : spec.id === "venue" ? "venue…" : "ticker or name…"}
            value={typeof current === "string" ? current : ""}
          />
        </div>
      );
    }
    const selected =
      current && typeof current === "object"
        ? spec.options.findIndex((option) => option.min === current.min && option.max === current.max)
        : -1;
    // A single-select listbox: one option in the tab order (the current
    // filter), arrows / Home / End move, Enter or Space applies and closes.
    const choices: Array<{ label: string; value: FilterValue | undefined }> = [
      { label: "All", value: undefined },
      ...spec.options.map((option) => ({ label: option.label, value: { min: option.min, max: option.max } })),
    ];
    const currentIndex = selected + 1;
    return (
      <div
        aria-label={`Filter ${spec.label}`}
        className={styles.menu}
        id={menuId}
        onKeyDown={(event) => {
          const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="option"]')];
          const from = items.indexOf(document.activeElement as HTMLButtonElement);
          const next = rovingIndex(event.key, from === -1 ? currentIndex : from, items.length, "vertical");
          if (next === null) return;
          event.preventDefault();
          items[next]?.focus();
        }}
        role="listbox"
      >
        {choices.map((choice, index) => (
          <button
            aria-selected={index === currentIndex}
            autoFocus={index === currentIndex}
            className={index === currentIndex ? styles.optionActive : styles.option}
            key={choice.label}
            onClick={() => {
              column.setFilterValue(choice.value);
              closeFilter(true);
            }}
            role="option"
            tabIndex={index === currentIndex ? 0 : -1}
            type="button"
          >
            {choice.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.board} ref={boardRef}>
      <div className={styles.toolbar}>
        <span className={styles.matchCount}>
          {visibleRows.length} of {rows.length} {rowNoun}
          {columnFilters.length ? (
            <button className={styles.clearFilters} onClick={() => setColumnFilters([])} type="button">
              clear filters
            </button>
          ) : null}
        </span>
        <label className={styles.flowWindow}>
          Net flow
          <select aria-label="Net flow window" onChange={(event) => setFlowWindow(event.target.value as FlowWindow)} value={flowWindow}>
            {FLOW_WINDOWS.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button className={styles.export} onClick={downloadCsv} type="button">
          Download CSV
        </button>
      </div>
      <div aria-label="Stock token data" className={openFilter ? styles.scrollMenuOpen : styles.scroll} role="region" tabIndex={0}>
        <table className={styles.table} style={{ width: table.getTotalSize() }}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const spec = specById.get(header.column.id);
                  const filtered = header.column.getFilterValue() !== undefined;
                  const sorted = header.column.getIsSorted();
                  const label = spec?.label ?? header.column.id;
                  return (
                    <th
                      aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"}
                      key={header.id}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => reorder(header.column.id)}
                      scope="col"
                      style={{ width: header.getSize() }}
                    >
                      <div className={styles.headerControls}>
                        <span
                          aria-hidden
                          className={styles.grip}
                          draggable
                          onDragStart={() => {
                            dragged.current = header.column.id;
                          }}
                          title="Drag to move this column"
                        >
                          ⠿
                        </span>
                        <button
                          className={sorted ? styles.headerButtonSorted : styles.headerButton}
                          onClick={header.column.getToggleSortingHandler()}
                          title={`Sort by ${label}`}
                          type="button"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <span aria-hidden className={styles.sortMark}>
                            {sorted === "desc" ? "↓" : sorted === "asc" ? "↑" : "↕"}
                          </span>
                        </button>
                        <button
                          aria-controls={openFilter === header.column.id ? menuId : undefined}
                          aria-expanded={openFilter === header.column.id}
                          aria-haspopup={spec && spec.kind !== "text" ? "listbox" : undefined}
                          aria-label={`${label} filter`}
                          className={styles.filterButton}
                          onClick={(event) => {
                            // Safari does not focus a clicked button; focus is restored here on close.
                            event.currentTarget.focus();
                            setOpenFilter(openFilter === header.column.id ? null : header.column.id);
                          }}
                          ref={(element) => {
                            if (element) filterButtons.current.set(header.column.id, element);
                            else filterButtons.current.delete(header.column.id);
                          }}
                          title={`Filter ${label}`}
                          type="button"
                        >
                          <Chevron active={filtered} />
                        </button>
                      </div>
                      <span
                        aria-hidden
                        className={styles.resizer}
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                      />
                      {openFilter === header.column.id && spec ? filterMenu(spec) : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.original.id}>
                {row.getVisibleCells().map((cell) => {
                  const value = cell.getValue();
                  const absent = cell.column.id !== "symbol" && (value === null || value === undefined);
                  return (
                    <td
                      className={absent ? styles.absent : undefined}
                      data-kind={specById.get(cell.column.id)?.kind}
                      key={cell.id}
                      style={{ width: cell.column.getSize() }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
